import { call, put, takeLatest, select } from 'redux-saga/effects';

import { putSession, graphql } from '../../../../globals/api/requests';
import { captureException } from '../../../../client/sentry';
import { isValidEmail } from '../../../account/helpers';

import {
  SOTY_VOTE,
  SOTY_TOKEN_RECEIVE,
  sotyVoteSuccess,
  sotyVoteFailed,
  sotyTokenReceived,
  sotyHistoryReceived,
} from '../actions';

export const stateSelector = state => state.areas.soty;

export async function generateToken(email) {
  const response = await graphql(
    `
      mutation shopOfTheYearVoteTokenCreate($email: String!) {
        shopOfTheYearVoteTokenCreate(email: $email) {
          token
          expires
          isUserToken
        }
      }
    `,
    { email }
  );
  return response.data.shopOfTheYearVoteTokenCreate;
}

export async function convertToken(token) {
  const tokenResponse = await graphql(
    `
      mutation shopOfTheYearVoteTokenConvert($token: String!) {
        shopOfTheYearVoteTokenConvert(token: $token) {
          token
          expires
          isUserToken
        }
      }
    `,
    { token }
  );
  return tokenResponse.data.shopOfTheYearVoteTokenConvert;
}

export function vote(token, category, storeId) {
  return graphql(
    `
      mutation shopOfTheYearVoteCreate(
        $category: Int
        $storeId: Int
        $token: String
      ) {
        shopOfTheYearVoteCreate(
          nominationCategoryId: $category
          storeId: $storeId
          token: $token
          clientId: 1331
        ) {
          nodes {
            id
            category {
              id
            }
            store {
              id
              name
            }
          }
        }
      }
    `,
    { token, category, storeId }
  );
}

function shouldConvertToken(token, user) {
  return (
    user && user.isAuthenticated && token && token.token && !token.isUserToken
  );
}

function hasTokenExpired(token) {
  return token.expires * 1000 < Date.now();
}

function isTokenValid(token, email) {
  return (
    token && token.token && token.email === email && !hasTokenExpired(token)
  );
}

export function* storeToken({ email, token }) {
  // Probably save this in its own cookie instead so we can expire it
  yield call(putSession, 'soty', {
    email,
    ...token,
  });
}

export function* castVote({ oldToken, email, user, categoryId, storeId }) {
  let tokenObject;

  const emailToUse = user && user.me && user.me.email ? user.me.email : email;

  try {
    if (emailToUse !== undefined && !isValidEmail(emailToUse)) {
      yield put(
        sotyVoteFailed({ errors: { email: ['validation.email-invalid'] } })
      );
      return;
    }
    if (shouldConvertToken(oldToken, user)) {
      tokenObject = yield call(convertToken, oldToken.token);
      yield put(sotyTokenReceived(tokenObject, emailToUse));
    } else if (isTokenValid(oldToken, emailToUse)) {
      tokenObject = oldToken;
    } else {
      tokenObject = yield call(generateToken, emailToUse);
      yield put(sotyTokenReceived(tokenObject, emailToUse));
    }
    const response = yield call(vote, tokenObject.token, categoryId, storeId);
    if (!response.errors) {
      yield put(sotyVoteSuccess(emailToUse, categoryId, storeId));
      yield put(
        sotyHistoryReceived(response.data.shopOfTheYearVoteCreate.nodes)
      );
    } else {
      yield put(
        sotyVoteFailed({
          errors: {
            form: [
              response.errors[0].message.indexOf('[#101]') === -1
                ? response.errors[0].code
                : 'registration.email-in-use',
            ],
          },
        })
      );
    }
  } catch (error) {
    yield call(captureException, error);
    yield put(sotyVoteFailed({ errors: { form: ['error.unknown'] } }));
  }
}

export function* removeToken() {
  yield call(putSession, 'soty', {});
}

export function* userReceive({ me }) {
  try {
    const soty = yield select(stateSelector);
    if (
      soty &&
      soty.soty_vote_popup &&
      soty.soty_vote_popup.token &&
      soty.soty_vote_popup.token.token &&
      !soty.soty_vote_popup.token.isUserToken
    ) {
      const token = yield call(convertToken, soty.soty_vote_popup.token.token);
      yield put(sotyTokenReceived(token, me.email));
    } else {
      const token = yield call(generateToken, me.email);
      yield put(sotyTokenReceived(token, me.email));
    }
  } catch (error) {
    yield call(captureException, error);
  }
}

export async function doFetchHistory(token) {
  const history = await graphql(
    `
      query($token: String) {
        shopOfTheYearVoteHistory(token: $token) {
          nodes {
            id
            category {
              id
            }
            store {
              id
              name
            }
          }
        }
      }
    `,
    { token: token.token }
  );
  return history.data.shopOfTheYearVoteHistory.nodes || [];
}

export function* onPopupOpened() {
  try {
    const soty = yield select(stateSelector);
    if (
      soty &&
      soty.soty_vote_popup &&
      soty.soty_vote_popup.token &&
      soty.soty_vote_popup.token.token &&
      !soty.soty_vote_popup.history
    ) {
      let token = soty.soty_vote_popup.token;
      if (hasTokenExpired(token)) {
        token = yield call(generateToken, soty.soty_vote_popup.token.email);
        yield put(sotyTokenReceived(token, soty.soty_vote_popup.token.email));
      }
      const history = yield call(doFetchHistory, token);
      yield put(sotyHistoryReceived(history));
    }
  } catch (error) {
    yield call(captureException, error);
  }
}

export function* fetchHistory({ token }) {
  try {
    const history = yield call(doFetchHistory, token);
    yield put(sotyHistoryReceived(history));
  } catch (error) {
    yield call(captureException, error);
  }
}

export function* mySaga() {
  const soty = yield select(stateSelector);
  if (!soty.isVotingOpen) {
    return;
  }
  yield takeLatest(SOTY_VOTE, castVote);
  yield takeLatest(SOTY_TOKEN_RECEIVE, storeToken);
  yield takeLatest(SOTY_TOKEN_RECEIVE, fetchHistory);
  yield takeLatest('LOGOUT_COMPLETE', removeToken);
  yield takeLatest('USER_RECEIVE', userReceive);
  yield takeLatest('APP_STARTUP', onPopupOpened);
}

if (IS_BROWSER) {
  window.registerSaga(mySaga);
}

export default mySaga;
